/**
 * Modal "Novidades" — aparece UMA vez após o login quando existem entradas
 * do ChangeLog publicadas que o usuário ainda não viu.
 *
 * Funcionamento:
 *  - No mount do Layout (usuário já autenticado), consulta
 *    GET /changelog/_/novidades. Vazio → não renderiza nada.
 *  - Fechar (botão, X ou clique no backdrop) marca o "visto" no servidor
 *    (usuarios.changelog_visto_em) — o modal só volta quando sair coisa
 *    nova depois disso. Falha ao marcar não bloqueia o fechamento.
 *  - Quem nunca viu recebe baseline fixo no backend (v2.17.1, 10/06/2026):
 *    ninguém é soterrado pelo histórico completo no primeiro login.
 */

import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import {
  changelogMarcarNovidadesVisto,
  changelogNovidades,
  type NovidadeChangelog,
} from "../lib/api.js";
import { Badge, Botao, type TomBadge } from "./ui/index.js";
import { formatarDataHoraBR } from "../lib/formato.js";

const TOM_CATEGORIA: Record<string, TomBadge> = {
  feature: "magenta",
  feat: "magenta",
  fix: "warning",
  bug_fix: "warning",
  integration: "info",
  docs: "info",
  rule: "ink",
};

const ROTULO_CATEGORIA: Record<string, string> = {
  feature: "novidade",
  feat: "novidade",
  fix: "correção",
  bug_fix: "correção",
  integration: "integração",
  docs: "documentação",
  rule: "regra",
};

/**
 * As descrições do ChangeLog são texto de governança (parágrafo único,
 * denso). Pro modal, reformatamos pra leitura humana:
 *  - enumerações "(1) ... (2) ..." viram lista numerada;
 *  - o resto quebra em parágrafos curtos (~2 frases cada).
 */
function formatarDescricao(texto: string): { paragrafos: string[]; itens: string[] } {
  const partes = texto.split(/\s*\(\d+\)\s+/);
  const intro = (partes[0] ?? "").trim();
  const itens =
    partes.length >= 3
      ? partes.slice(1).map((s) => s.trim().replace(/[;.]\s*$/, "."))
      : [];
  const corpo = itens.length > 0 ? intro : texto;

  // Quebra em frases por ". " e agrupa de duas em duas.
  const frases = corpo
    .split(/\.\s+/)
    .map((s, i, arr) => (i < arr.length - 1 ? `${s}.` : s))
    .filter((s) => s.trim().length > 0);
  const paragrafos: string[] = [];
  for (let i = 0; i < frases.length; i += 2) {
    paragrafos.push(frases.slice(i, i + 2).join(" "));
  }
  return { paragrafos, itens };
}

export function NovidadesModal() {
  const [novidades, setNovidades] = useState<NovidadeChangelog[]>([]);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    let ativo = true;
    void changelogNovidades()
      .then((r) => {
        if (ativo && r.novidades.length > 0) {
          setNovidades(r.novidades);
          setAberto(true);
        }
      })
      .catch(() => {
        /* sem novidades nesta sessão — nunca bloquear o uso do sistema */
      });
    return () => {
      ativo = false;
    };
  }, []);

  const fechar = () => {
    setAberto(false);
    // Fire-and-forget: fechar É marcar como visto. Se a chamada falhar o
    // modal pode reaparecer no próximo login — preferível a travar o fluxo.
    void changelogMarcarNovidadesVisto().catch(() => {});
  };

  if (!aberto || novidades.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={fechar}
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-lg shadow-2xl bg-white dark:bg-ink-900 border border-ink-200 dark:border-ink-700"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Novidades do Sentinela"
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-ink-100 dark:border-ink-700">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-magenta-600" />
            <div>
              <h2 className="text-lg font-semibold">Novidades do Sentinela</h2>
              <p className="text-xs text-ink-600">
                {novidades.length === 1
                  ? "1 atualização desde sua última visita"
                  : `${novidades.length} atualizações desde sua última visita`}
              </p>
            </div>
          </div>
          <button
            onClick={fechar}
            className="p-1.5 rounded hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-600"
            title="Fechar e marcar como visto"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {novidades.map((n, i) => (
            <details
              key={n.id}
              open={i === 0}
              className="rounded border border-ink-200 dark:border-ink-700 group"
            >
              <summary className="cursor-pointer select-none px-3 py-2.5 hover:bg-ink-50 dark:hover:bg-ink-800 rounded">
                <div className="inline-flex flex-wrap items-center gap-2 align-middle">
                  <Badge tom={TOM_CATEGORIA[n.categoria] ?? "ink"}>
                    {ROTULO_CATEGORIA[n.categoria] ?? n.categoria}
                  </Badge>
                  <Badge tom="ink">{n.versao}</Badge>
                  <span className="text-xs text-ink-600">
                    {formatarDataHoraBR(n.criado_em)}
                  </span>
                </div>
                <div className="font-medium mt-1">{n.titulo}</div>
              </summary>
              <div className="px-3 pb-3 pt-1 space-y-3">
                {/* O "impacto" é a versão humana — vem primeiro e em
                    destaque. A descrição técnica completa fica recolhida. */}
                {n.impacto && (
                  <p className="text-sm leading-relaxed rounded bg-magenta-50 dark:bg-magenta-900/20 border-l-2 border-magenta-500 px-3 py-2">
                    {n.impacto}
                  </p>
                )}
                <DescricaoFormatada
                  texto={n.descricao}
                  recolhida={!!n.impacto}
                />
              </div>
            </details>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-ink-100 dark:border-ink-700">
          <Botao variante="primary" tamanho="sm" onClick={fechar}>
            Entendi
          </Botao>
        </div>
      </div>
    </div>
  );
}

/** Descrição técnica formatada — parágrafos curtos + lista numerada.
 *  Quando a entrada tem "Na prática", fica recolhida atrás de um toggle. */
function DescricaoFormatada({ texto, recolhida }: { texto: string; recolhida: boolean }) {
  const { paragrafos, itens } = formatarDescricao(texto);
  const corpo = (
    <div className="space-y-2 text-sm leading-relaxed text-ink-600 dark:text-ink-300">
      {paragrafos.map((par, i) => (
        <p key={i}>{par}</p>
      ))}
      {itens.length > 0 && (
        <ol className="list-decimal pl-5 space-y-1.5">
          {itens.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      )}
    </div>
  );
  if (!recolhida) return corpo;
  return (
    <details>
      <summary className="text-xs text-ink-600 cursor-pointer select-none hover:text-magenta-600">
        Detalhes técnicos
      </summary>
      <div className="mt-2">{corpo}</div>
    </details>
  );
}
